/**
 * Pause/play control for autoplaying background videos (WCAG 2.2.2).
 *
 * Handles two kinds of players:
 *  - native <video autoplay> without controls (feature carousels, banners, galleries)
 *  - the YouTube hero banner (.video-overlay iframe created by YT.Player)
 *
 * Placement: centered on product pages, bottom-right everywhere else.
 */
(function () {
  if (window.__videoPauseControlInit) return;
  window.__videoPauseControlInit = true;

  var nativePlay = HTMLMediaElement.prototype.play;
  var tracked = new WeakSet();
  var ownedButtons = new WeakSet();

  // Clips shorter than this don't get a pause control
  var MIN_DURATION_SECONDS = 4;

  var ICONS = {
    pause:
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 5h4v14H6zM14 5h4v14h-4z"/></svg>',
    play:
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M8 5v14l11-7z"/></svg>',
  };

  function noop() {}

  function isProductPage() {
    return document.body.classList.contains('product-page');
  }

  function createButton(placement) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'video-pause-btn video-pause-btn--' + placement;
    ownedButtons.add(btn);
    renderState(btn, true);
    return btn;
  }

  function renderState(btn, playing) {
    btn.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
    btn.innerHTML = playing ? ICONS.pause : ICONS.play;
  }

  // Swiper clones slides with cloneNode, which would copy buttons we already added
  function removeClonedButtons(host) {
    host.querySelectorAll(':scope > .video-pause-btn').forEach(function (btn) {
      if (!ownedButtons.has(btn)) btn.remove();
    });
  }

  function hideFromTabOrderIfDuplicate(el, btn) {
    if (el.closest('.swiper-slide-duplicate, [aria-hidden="true"]')) {
      btn.tabIndex = -1;
      btn.setAttribute('aria-hidden', 'true');
    }
  }

  function ensurePositioned(host) {
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
  }

  /* Native <video> ------------------------------------------------------- */

  function setupNativeVideo(video) {
    if (tracked.has(video)) return;
    tracked.add(video);

    var host = video.parentElement;
    if (!host) return;

    var inFeatures = !!video.closest('.product-features-carousel, .product-features-cards');
    var placement = inFeatures || isProductPage() ? 'center' : 'corner';

    removeClonedButtons(host);
    ensurePositioned(host);

    // Several theme scripts call play() whenever the video pauses or scrolls into view.
    // Ignore those calls while the user has paused the video.
    video.play = function () {
      if (video.dataset.userPaused === 'true') return Promise.resolve();
      return nativePlay.apply(video, arguments);
    };

    var btn = createButton(placement);
    hideFromTabOrderIfDuplicate(video, btn);
    video.insertAdjacentElement('afterend', btn);

    function centerOnVideo() {
      btn.style.left = video.offsetLeft + video.offsetWidth / 2 + 'px';
      btn.style.top = video.offsetTop + video.offsetHeight / 2 + 'px';
    }

    if (placement === 'center') {
      centerOnVideo();
      if ('ResizeObserver' in window) {
        var ro = new ResizeObserver(centerOnVideo);
        ro.observe(video);
        ro.observe(host);
      } else {
        window.addEventListener('resize', centerOnVideo);
      }
      video.addEventListener('loadedmetadata', centerOnVideo);
    }

    // Duration is only known after metadata loads; Infinity (live streams) keeps the button
    function toggleForDuration() {
      if (!video.duration || isNaN(video.duration)) return;
      btn.classList.toggle('video-pause-btn--short', video.duration < MIN_DURATION_SECONDS);
    }
    toggleForDuration();
    video.addEventListener('loadedmetadata', toggleForDuration);
    video.addEventListener('durationchange', toggleForDuration);

    btn.addEventListener('click', function () {
      var paused = video.dataset.userPaused === 'true';
      if (paused) {
        delete video.dataset.userPaused;
        nativePlay.call(video).catch(noop);
      } else {
        video.dataset.userPaused = 'true';
        video.pause();
      }
      renderState(btn, paused);
    });
  }

  /* YouTube iframes ------------------------------------------------------ */

  function postToPlayer(iframe, func) {
    if (!iframe.contentWindow) return;
    iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func: func, args: [] }), '*');
  }

  function setupYouTube(iframe, host, placement) {
    if (tracked.has(iframe)) return;
    tracked.add(iframe);

    ensurePositioned(host);
    removeClonedButtons(host);

    var btn = createButton(placement);
    host.appendChild(btn);

    // Banner CTAs can sit in the same corner: stack the button above them instead of overlapping
    function avoidCtaOverlap() {
      var banner = host.closest('.banner');
      if (placement !== 'corner' || !banner) return;
      btn.style.removeProperty('bottom');
      var btnRect = btn.getBoundingClientRect();
      var hostRect = host.getBoundingClientRect();
      var gap = 12;
      var lowestTop = null;
      banner.querySelectorAll('.button-primary, .button').forEach(function (cta) {
        var r = cta.getBoundingClientRect();
        var overlaps =
          r.width > 0 && r.left < btnRect.right && r.right > btnRect.left && r.top < btnRect.bottom && r.bottom > btnRect.top;
        if (overlaps && (lowestTop === null || r.top < lowestTop)) lowestTop = r.top;
      });
      if (lowestTop !== null) {
        btn.style.setProperty('bottom', hostRect.bottom - lowestTop + gap + 'px', 'important');
      }
    }
    avoidCtaOverlap();
    if ('ResizeObserver' in window) new ResizeObserver(avoidCtaOverlap).observe(host);
    window.addEventListener('load', avoidCtaOverlap);

    btn.addEventListener('click', function () {
      var paused = host.dataset.userPaused === 'true';
      if (paused) {
        delete host.dataset.userPaused;
        postToPlayer(iframe, 'playVideo');
      } else {
        host.dataset.userPaused = 'true';
        postToPlayer(iframe, 'pauseVideo');
      }
      renderState(btn, paused);
    });

    // YouTube flashes its own controls (prev/pause/next) for a few seconds when the hero starts:
    // keep our button hidden until they are gone
    if (placement === 'corner' && host.classList.contains('banner__media')) {
      var STARTUP_OVERLAY_MS = 3500;
      var revealed = false;
      var reveal = function () {
        if (revealed) return;
        revealed = true;
        host.classList.remove('video-yt-starting');
      };
      host.classList.add('video-yt-starting');
      setTimeout(reveal, 8000);
      window.addEventListener('message', function onStart(event) {
        if (event.source !== iframe.contentWindow || typeof event.data !== 'string') return;
        try {
          var data = JSON.parse(event.data);
          var state = data.event === 'onStateChange' ? data.info : data.info && data.info.playerState;
          if (state === 1) {
            window.removeEventListener('message', onStart);
            setTimeout(reveal, STARTUP_OVERLAY_MS);
          }
        } catch (e) {
          /* not a YouTube payload */
        }
      });
    }

    // Keep the icon in sync with the real player state (e.g. autoplay blocked by the browser)
    window.addEventListener('message', function (event) {
      if (event.source !== iframe.contentWindow || typeof event.data !== 'string') return;
      var data;
      try {
        data = JSON.parse(event.data);
      } catch (e) {
        return;
      }
      var state = data.event === 'onStateChange' ? data.info : data.info && data.info.playerState;
      if (state === 1) renderState(btn, true);
      else if (state === 2) renderState(btn, false);
    });

    function listen() {
      if (iframe.contentWindow) {
        iframe.contentWindow.postMessage(JSON.stringify({ event: 'listening', id: 1, channel: 'widget' }), '*');
      }
    }
    iframe.addEventListener('load', listen);
    listen();
  }

  function isJsApiYouTube(iframe) {
    var src = iframe.getAttribute('src') || '';
    return src.indexOf('youtube.com/embed') !== -1 && src.indexOf('enablejsapi=1') !== -1;
  }

  /* Discovery ------------------------------------------------------------ */

  function scan() {
    document.querySelectorAll('video[autoplay]:not([controls])').forEach(setupNativeVideo);

    document.querySelectorAll('.video-overlay iframe').forEach(function (iframe) {
      if (isJsApiYouTube(iframe)) setupYouTube(iframe, iframe.closest('.banner__media') || iframe.parentElement, 'corner');
    });
  }

  var scanTimer;
  function scheduleScan() {
    clearTimeout(scanTimer);
    scanTimer = setTimeout(scan, 100);
  }

  function init() {
    scan();
    new MutationObserver(scheduleScan).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
